import { Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { SplashScreen } from "@/components/layout/SplashScreen";
import {
  CapabilityRoute,
  OnboardingGate,
  ProtectedRoute,
  PublicOnlyRoute,
  SubscriptionGate,
} from "@/routes/guards";
import {
  PlatformProvider,
  PlatformPublicRoute,
  PlatformRoute,
} from "@/features/platform/components/PlatformProvider";
import { PlatformShell } from "@/features/platform/components/PlatformShell";
import { ForgotPasswordPage } from "@/features/auth/pages/ForgotPasswordPage";
import { LoginPage } from "@/features/auth/pages/LoginPage";
import { RegisterPage } from "@/features/auth/pages/RegisterPage";
import { ResetPasswordPage } from "@/features/auth/pages/ResetPasswordPage";
import { DashboardPage } from "@/features/dashboard/pages/DashboardPage";
import { ForbiddenPage, NotFoundPage } from "@/routes/ErrorPages";
import {
  AccountingPage,
  ActivityLogPage,
  BillingPage,
  CategoriesPage,
  CustomersPage,
  ExpenseCategoriesPage,
  ExpensesPage,
  PosPage,
  ProductsPage,
  RegistersPage,
  ProfilePage,
  SecurityPage,
  TablesPage,
  ReportsPage,
  SalesPage,
  OnboardingPage,
  OutletsPage,
  PurchaseReturnsPage,
  PurchasesPage,
  ReturnsPage,
  RolesPage,
  SettingsPage,
  StaffPage,
  SupportPage,
  SupportTicketPage,
  StockAdjustmentsPage,
  StockPage,
  SupplierPaymentsPage,
  SuppliersPage,
  TaxesPage,
  TransfersPage,
  PlatformAdminsPage,
  PlatformDashboardPage,
  PlatformLoginPage,
  PlatformPlansPage,
  PlatformSettingsPage,
  PlatformSupportPage,
  PlatformSupportTicketPage,
  PlatformSubscriptionsPage,
  PlatformTenantDetailPage,
  PlatformTenantsPage,
  PlatformWebhooksPage,
} from "@/routes/lazyScreens";

/**
 * Guards compose outside-in:
 *
 *   ProtectedRoute   → is there a resolved session?
 *   OnboardingGate   → has this workspace finished setting up?
 *   SubscriptionGate → is the workspace allowed to trade?
 *   CapabilityRoute  → may THIS user open THIS screen?
 *
 * Route-level capability checks are cosmetic, exactly like the sidebar — the
 * server re-checks every one of them. What they buy is a user who never lands
 * on a screen that then 403s at them.
 *
 * Everything except the dashboard is lazy. The POS and the dashboard are the
 * two screens people open constantly; reports drags in recharts, and the rest
 * is long-tail admin that should not sit in the bundle a cashier waits on.
 */

/** A lazy console screen. No capability gate — the console is all-or-nothing. */
function consoleScreen(element: ReactNode) {
  return <Suspense fallback={<SplashScreen />}>{element}</Suspense>;
}

/** A lazy screen behind its capability gate, with a splash while it loads. */
function screen(cap: string | string[], element: ReactNode) {
  return (
    <CapabilityRoute cap={cap}>
      <Suspense fallback={<SplashScreen />}>{element}</Suspense>
    </CapabilityRoute>
  );
}

export const router = createBrowserRouter([
  {
    element: <PublicOnlyRoute />,
    children: [
      { path: "/login", element: <LoginPage /> },
      { path: "/register", element: <RegisterPage /> },
      { path: "/forgot-password", element: <ForgotPasswordPage /> },
      { path: "/reset-password", element: <ResetPasswordPage /> },
    ],
  },

  {
    element: <ProtectedRoute />,
    children: [
      {
        /*
         * Above SubscriptionGate, and above the wizard route itself, because
         * it redirects BOTH ways: an unfinished workspace into the wizard, and
         * a finished one back out of it.
         *
         * It has to sit outside SubscriptionGate: that gate owns the redirect
         * to /billing, and a shop halfway through setup must not be bounced
         * there mid-question.
         */
        element: <OnboardingGate />,
        children: [
          {
            path: "/onboarding",
            element: (
              <Suspense fallback={<SplashScreen />}>
                <OnboardingPage />
              </Suspense>
            ),
          },
          {
            element: <SubscriptionGate />,
            children: [
              // The POS sits OUTSIDE AppShell: it takes the full viewport with no
              // sidebar, because a cashier is not navigating, they are selling.
              { path: "/pos", element: screen("pos.access", <PosPage />) },

              {
                element: <AppShell />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="/dashboard" replace />,
                  },
                  { path: "/dashboard", element: <DashboardPage /> },
                  /*
                   * Open orders on the restaurant floor. Guarded on pos.access
                   * like every other held-sales surface, because that is what
                   * the API enforces — the `tables` module decides whether the
                   * NAV shows it, and a module is not a permission.
                   */
                  { path: "/tables", element: screen("pos.access", <TablesPage />) },

                  /*
                   * Your own account. No capability gate: every signed-in user
                   * may read and change their own name and password, and a
                   * cashier locked out of their own security page is a
                   * cashier who cannot rotate a password somebody watched
                   * them type.
                   */
                  { path: "/settings/profile", element: consoleScreen(<ProfilePage />) },
                  { path: "/settings/security", element: consoleScreen(<SecurityPage />) },

                  {
                    path: "/products",
                    element: screen("products.view", <ProductsPage />),
                  },
                  {
                    path: "/categories",
                    element: screen("categories.manage", <CategoriesPage />),
                  },
                  {
                    path: "/inventory",
                    element: screen("inventory.view", <StockPage />),
                  },
                  {
                    path: "/taxes",
                    element: screen("taxes.manage", <TaxesPage />),
                  },
                  {
                    path: "/customers",
                    element: screen("customers.view", <CustomersPage />),
                  },
                  {
                    path: "/suppliers",
                    element: screen("suppliers.manage", <SuppliersPage />),
                  },
                  {
                    path: "/purchases",
                    element: screen("purchases.view", <PurchasesPage />),
                  },
                  {
                    // Same plan feature as Purchases — the routes carry
                    // `feature:purchases`, so without it this 403s.
                    path: "/purchase-returns",
                    element: screen("purchases.view", <PurchaseReturnsPage />),
                  },
                  {
                    path: "/inventory/adjustments",
                    element: screen("inventory.view", <StockAdjustmentsPage />),
                  },
                  {
                    path: "/transfers",
                    element: screen("inventory.transfer", <TransfersPage />),
                  },
                  {
                    path: "/returns",
                    element: screen("sales.return", <ReturnsPage />),
                  },
                  {
                    path: "/sales",
                    element: screen(
                      ["sales.view_own", "sales.view_all"],
                      <SalesPage />,
                    ),
                  },
                  {
                    path: "/registers",
                    element: screen(
                      ["registers.open", "registers.view_all"],
                      <RegistersPage />,
                    ),
                  },
                  {
                    path: "/reports",
                    element: screen(
                      [
                        "reports.sales",
                        "reports.profit",
                        "reports.inventory",
                        "reports.tax",
                      ],
                      <ReportsPage />,
                    ),
                  },
                  {
                    path: "/staff",
                    element: screen("staff.view", <StaffPage />),
                  },
                  {
                    path: "/roles",
                    element: screen("roles.manage", <RolesPage />),
                  },
                  {
                    path: "/outlets",
                    element: screen("outlets.manage", <OutletsPage />),
                  },
                  {
                    path: "/settings",
                    element: screen("settings.manage", <SettingsPage />),
                  },
                  {
                    path: "/billing",
                    element: screen("billing.manage", <BillingPage />),
                  },

                  // --- Finance -----------------------------------------
                  {
                    path: "/expenses",
                    element: screen("expenses.view", <ExpensesPage />),
                  },
                  {
                    // Either capability opens the list; only the writes need
                    // `manage`, and the buttons gate themselves on it.
                    path: "/expense-categories",
                    element: screen(
                      ["expenses.view", "expense_categories.manage"],
                      <ExpenseCategoriesPage />,
                    ),
                  },
                  {
                    // Reading needs suppliers.manage; recording a payment
                    // needs supplier_payments.manage, gated on the button.
                    path: "/supplier-payments",
                    element: screen("suppliers.manage", <SupplierPaymentsPage />),
                  },
                  {
                    path: "/accounting",
                    element: screen("accounting.view", <AccountingPage />),
                  },
                  {
                    path: "/activity-log",
                    element: screen("activity.view", <ActivityLogPage />),
                  },

                  // Either capability opens these; the server narrows the rows.
                  // Somebody with support.manage alone sees the threads they
                  // raised, which is what makes this reachable by a cashier.
                  {
                    path: "/support",
                    element: screen(
                      ["support.view", "support.manage"],
                      <SupportPage />,
                    ),
                  },
                  {
                    path: "/support/:reference",
                    element: screen(
                      ["support.view", "support.manage"],
                      <SupportTicketPage />,
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  /*
   * The operator console.
   *
   * A SIBLING of the tenant app, not a branch inside it. Different principal,
   * different token, different store, different HTTP client — the only thing
   * shared is the bundle. PlatformProvider wraps the whole subtree so the
   * platform client is configured and /me resolved before any screen renders,
   * and none of that machinery exists for a user who never comes here.
   */
  {
    path: "/platform",
    element: (
      <PlatformProvider>
        <Outlet />
      </PlatformProvider>
    ),
    children: [
      {
        element: <PlatformPublicRoute />,
        children: [
          { path: "login", element: consoleScreen(<PlatformLoginPage />) },
        ],
      },
      {
        element: <PlatformRoute />,
        children: [
          {
            element: <PlatformShell />,
            children: [
              {
                index: true,
                element: consoleScreen(<PlatformDashboardPage />),
              },
              {
                path: "tenants",
                element: consoleScreen(<PlatformTenantsPage />),
              },
              {
                path: "tenants/:uuid",
                element: consoleScreen(<PlatformTenantDetailPage />),
              },
              { path: "plans", element: consoleScreen(<PlatformPlansPage />) },
              {
                path: "subscriptions",
                element: consoleScreen(<PlatformSubscriptionsPage />),
              },
              {
                path: "webhooks",
                element: consoleScreen(<PlatformWebhooksPage />),
              },
              {
                path: "admins",
                element: consoleScreen(<PlatformAdminsPage />),
              },
              {
                path: "support",
                element: consoleScreen(<PlatformSupportPage />),
              },
              {
                path: "support/:reference",
                element: consoleScreen(<PlatformSupportTicketPage />),
              },
              {
                path: "settings",
                element: consoleScreen(<PlatformSettingsPage />),
              },
            ],
          },
        ],
      },
    ],
  },

  { path: "/403", element: <ForbiddenPage /> },
  { path: "*", element: <NotFoundPage /> },
]);
